import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useEditableLesson } from '@/data/hook/lesson/useEditableLesson';
import useLessonReplacement from '@/data/hook/lesson/useLessonReplacement';
import useLessonWorkspace from '@/data/hook/lesson/useLessonWorkspace';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import { createBook } from '@/data/services/bookServices';
import { createChapter } from '@/data/services/chapterServices';
import { lessonsQueryKeys } from '@/data/services/querykeys';
import { BookStyleEnum, bookType } from '@/models/Book';
import Lesson from '@/models/Lesson';
import { toLessonBookPatch } from '@/utils/book/lessonBookPatch';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ROUTES } from '@/utils/routes';

type UseCreateTextMaterialParams = {
  lessonId: number;
  bookType: bookType;
};

const useCreateTextMaterial = ({
  lessonId,
  bookType,
}: UseCreateTextMaterialParams) => {
  const { courseLineId, moduleId, slug = '', unitId } = useLessonWorkspace();
  const { runEditable } = useEditableLesson(lessonId);
  const { syncLesson } = useLessonReplacement();
  const lessonCache = useSingleCache<Lesson>(
    lessonsQueryKeys.get(lessonId).queryKey,
  );
  const navigate = useNavigate();

  const createMaterial = async (editableLessonId: number) => {
    const params = { lessonId: editableLessonId, bookType };
    const book = await createBook(params);
    const chapter = await createChapter(
      { ...params, bookId: book.id },
      { content: '' },
    );
    return {
      lessonId: editableLessonId,
      bookId: book.id,
      chapterId: chapter.id,
    };
  };

  const { mutate: createTextMaterial, isPending } = useMutation({
    mutationFn: async () => {
      const material = await runEditable(createMaterial, {
        skipOnVersioned: true,
      });
      if (material.lessonId !== lessonId) await syncLesson(material.lessonId);
      else
        lessonCache.merge(
          toLessonBookPatch(bookType, {
            id: material.bookId,
            bookStyle: BookStyleEnum.TEXT,
          }),
        );
      return material;
    },
    onSuccess: ({ lessonId, bookId, chapterId }) =>
      navigate(
        ROUTES.ADMIN.COURSE_LINES.MATERIAL_CHAPTER({
          courseLineId,
          moduleId,
          slug,
          unitId,
          lessonId,
          bookType,
          bookId,
          chapterId,
        }),
      ),
    onError: error => alert.error(getErrorMessage(error)),
  });

  return { createTextMaterial, isPending };
};

export default useCreateTextMaterial;
